(function () {
  const AGE_RULES = {
    expecting: { label: "Expecting", autoSize: "160ml", chargeLimit: 6 },
    "0-1mo": { label: "0-1 month", autoSize: "160ml", chargeLimit: 6 },
    "1-2mo": { label: "1-2 month", autoSize: "160ml", chargeLimit: 5 },
    "2-3mo": { label: "2-3 month", autoSize: "160ml", chargeLimit: 4, upgradeNext: true },
    "3-4mo": { label: "3-4 month", autoSize: "160ml", chargeLimit: 3, upgradeNext: true },
    "4-5mo": { label: "4-5 month", autoSize: "240ml", chargeLimit: 4 },
    "5-6mo": { label: "5-6 month", autoSize: "240ml", chargeLimit: 3 },
    "6+mo": { label: "6+ month", autoSize: "240ml", chargeLimit: null }
  };

  const FLOW_BY_SIZE = {
    "160ml": "sip",
    "240ml": "stream"
  };

  const SIZE_LABEL = {
    "160ml": "5oz",
    "240ml": "8oz"
  };

  const FLOW_LABEL = {
    drop: "Drop",
    sip: "Sip",
    stream: "Stream",
    rapid: "Rapid" 
  };

  class PdpConfigurator extends HTMLElement {
    connectedCallback() {
      this.config = this.readConfig();
      this.form = document.getElementById(this.config.formId);
      this.state = {
        age: "0-1mo",
        subscribe: true,
        mode: "auto",
        style: "wrapped_glass",
        size: "160ml",
        dueDate: "",
        duo: false,
        ventingAddon: false,
        agesExpanded: false
      };

      this.bind();
      this.render();
    }

    readConfig() {
      const script = this.querySelector("[data-pdp-config]");
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return { variants: [], metafields: {}, images: {} };
      }
    }

    bind() {
      this.querySelectorAll("[data-age]").forEach((button) => {
        button.addEventListener("click", () => this.selectAge(button.dataset.age));
      });

      this.querySelector("[data-pdp-toggle-ages]")?.addEventListener("click", () => {
        this.state.agesExpanded = !this.state.agesExpanded;
        this.render();
      });

      this.querySelector("[data-pdp-toggle-manual]")?.addEventListener("click", () => {
        this.state.mode = this.state.mode === "manual" ? "auto" : "manual";
        if (this.state.mode === "manual") {
          this.state.size = this.derivedAutoSize();
        }
        this.render();
      });

      this.querySelectorAll("[data-pdp-style]").forEach((button) => {
        button.addEventListener("click", () => {
          this.state.mode = "manual";
          this.state.style = button.dataset.pdpStyle;
          if (this.state.style === "glass") {
            this.state.subscribe = false;
            this.state.duo = false;
          }
          this.render();
        });
      });

      this.querySelectorAll("[data-pdp-size]").forEach((button) => {
        button.addEventListener("click", () => {
          this.state.mode = "manual";
          this.state.size = button.dataset.pdpSize;
          this.render();
        });
      });

      this.querySelector("[data-pdp-one-time]")?.addEventListener("change", (event) => {
        this.state.subscribe = !event.target.checked;
        if (this.state.style === "glass") {
          this.state.subscribe = false;
        }
        this.render();
      });

      this.querySelector("[data-pdp-venting-addon]")?.addEventListener("change", (event) => {
        this.state.ventingAddon = event.target.checked;
        this.render();
      });

      this.querySelector("[data-pdp-duo-toggle]")?.addEventListener("click", () => {
        this.state.duo = !this.state.duo;
        this.render();
      });

      this.querySelectorAll("[data-pdp-due-month], [data-pdp-due-day], [data-pdp-due-year]").forEach((input) => {
        input.addEventListener("input", () => {
          input.value = input.value.replace(/\D/g, "");
          this.validateDueDate();
        });
      });
      this.querySelectorAll("[data-pdp-due-cancel]").forEach((button) => {
        button.addEventListener("click", () => this.closeDueModal(false));
      });
      this.querySelector("[data-pdp-due-modal]")?.addEventListener("click", (event) => {
        if (event.target === event.currentTarget) {
          this.closeDueModal(false);
        }
      });
      this.querySelector("[data-pdp-due-modal] form")?.addEventListener("submit", (event) => {
        event.preventDefault();
        if (this.validateDueDate()) {
          this.closeDueModal(true);
        }
      });

      this.form?.addEventListener("submit", (event) => this.onSubmit(event), true);
    }

    selectAge(age) {
      if (age === "expecting") {
        this.openDueModal();
        return;
      }

      this.state.age = age;
      this.state.dueDate = "";
      this.state.mode = "auto";
      this.state.style = "wrapped_glass";
      this.state.size = this.derivedAutoSize();
      this.state.subscribe = true;
      this.state.ventingAddon = false;
      this.render();
    }

    openDueModal() {
      const modal = this.querySelector("[data-pdp-due-modal]");
      this.populateDueFields(this.state.dueDate);
      this.validateDueDate();
      if (modal?.showModal) {
        modal.showModal();
      }
    }

    closeDueModal(save) {
      const modal = this.querySelector("[data-pdp-due-modal]");
      const dueDate = this.readDueDateFromFields();

      if (save && dueDate) {
        this.state.age = "expecting";
        this.state.dueDate = dueDate;
        this.state.mode = "auto";
        this.state.style = "wrapped_glass";
        this.state.size = "160ml";
        this.state.subscribe = true;
      }

      modal?.close();
      this.render();
    }

    validateDueDate() {
      const submit = this.querySelector("[data-pdp-due-submit]");
      const error = this.querySelector("[data-pdp-due-error]");
      const value = this.readDueDateFromFields();
      const valid = value && new Date(`${value}T00:00:00`) > new Date(new Date().toDateString());

      if (submit) {
        submit.disabled = !valid;
      }
      if (error) {
        error.hidden = !value || valid;
      }

      return Boolean(valid);
    }

    populateDueFields(value) {
      const month = this.querySelector("[data-pdp-due-month]");
      const day = this.querySelector("[data-pdp-due-day]");
      const year = this.querySelector("[data-pdp-due-year]");

      if (!value) {
        if (month) month.value = "";
        if (day) day.value = "";
        if (year) year.value = "";
        return;
      }

      const parts = value.split("-");
      if (year) year.value = parts[0] || "";
      if (month) month.value = parts[1] || "";
      if (day) day.value = parts[2] || "";
    }

    readDueDateFromFields() {
      const month = this.querySelector("[data-pdp-due-month]")?.value.padStart(2, "0");
      const day = this.querySelector("[data-pdp-due-day]")?.value.padStart(2, "0");
      const year = this.querySelector("[data-pdp-due-year]")?.value;

      if (!month || !day || !year || year.length !== 4) {
        return "";
      }

      const date = new Date(`${year}-${month}-${day}T00:00:00`);
      if (date.getFullYear() !== Number(year) || date.getMonth() + 1 !== Number(month) || date.getDate() !== Number(day)) {
        return "";
      }

      return `${year}-${month}-${day}`;
    }

    derivedAutoSize() {
      return AGE_RULES[this.state.age]?.autoSize || "160ml";
    }

    currentSize() {
      return this.state.mode === "manual" ? this.state.size : this.derivedAutoSize();
    }

    currentFlow() {
      return FLOW_BY_SIZE[this.currentSize()];
    }

    bonusFlow() {
      return this.currentFlow() === "stream" ? "sip" : "drop";
    }

    currentVariant() {
      const size = this.currentSize();
      const style = this.state.style;
      const fromMetafield = this.variantIdFromMetafield(style, size);

      if (fromMetafield) {
        return { id: fromMetafield, price: this.selectedProductVariant()?.price || 0 };
      }

      return this.findVariant(style, size) || this.selectedProductVariant();
    }

    selectedProductVariant() {
      return (this.config.variants || []).find((variant) => variant.available) || (this.config.variants || [])[0];
    }

    variantIdFromMetafield(style, size) {
      const key = `${style === "glass" ? "glass" : "wrapped"}${size === "240ml" ? "240" : "160"}VariantId`;
      return this.config.metafields?.[key];
    }

    findVariant(style, size) { 
      return (this.config.variants || []).find((variant) => {
        const haystack = `${variant.title || ""} ${(variant.options || []).join(" ")}`.toLowerCase();
        const isWrapped = haystack.includes("wrapped") || haystack.includes("shield") || haystack.includes("armored");
        const isGlass = haystack.includes("glass") || haystack.includes("naked") || haystack.includes("pure");
        const sizeMatches = size === "160ml" ? /1\s*60|160|5\s?oz/.test(haystack) : /240|8\s?oz/.test(haystack);

        if (!sizeMatches) {
          return false;
        }

        if (style === "wrapped_glass") {
          return isWrapped || (!isGlass && !haystack.includes("pure"));
        }

        return isGlass && !isWrapped;
      });
    }

    render() {
      const ageRule = AGE_RULES[this.state.age];
      const size = this.currentSize();
      const flow = this.currentFlow();
      const variant = this.currentVariant();
      const oneTimeInput = this.querySelector("[data-pdp-one-time]");
      const submitText = this.querySelector("[data-pdp-submit-text]");

      this.querySelector("[data-pdp-selected-age-label]").textContent = this.ageLabel();

      this.querySelectorAll("[data-age]").forEach((button) => {
        const selected = button.dataset.age === this.state.age;
        button.setAttribute("aria-pressed", selected ? "true" : "false");
        if (button.classList.contains("is-extra")) {
          button.hidden = !this.state.agesExpanded && !selected;
        }
      });

      const ageToggle = this.querySelector("[data-pdp-toggle-ages]");
      if (ageToggle) {
        ageToggle.textContent = this.state.agesExpanded ? "Show fewer ages" : "See all ages";
      }

      const expectingSubtitle = this.querySelector("[data-pdp-expecting-subtitle]");
      if (expectingSubtitle) {
        expectingSubtitle.textContent = this.state.dueDate ? `Due ${this.formatShortDate(this.state.dueDate)}` : "Due date";
      }

      this.renderManualPanel(ageRule, size);
      this.renderBenefits(size, flow);

      if (oneTimeInput) {
        oneTimeInput.checked = !this.state.subscribe;
        oneTimeInput.disabled = this.state.style === "glass";
      }

      this.querySelector("[data-pdp-venting-row]").hidden = this.state.subscribe;
      this.querySelector("[data-pdp-duo-card]").hidden = this.state.style === "glass";
      this.querySelector("[data-pdp-duo-card]").classList.toggle("is-active", this.state.duo);
      this.querySelector("[data-pdp-duo-toggle]").textContent = this.state.duo ? "Duo added" : "Get the Duo";

      if (submitText) {
        submitText.innerHTML = this.buttonText(variant);
      }

      this.syncFields();
    }

    ageLabel() {
      if (this.state.age === "expecting" && this.state.dueDate) {
        return `Expecting - due ${this.formatShortDate(this.state.dueDate)}`;
      }
      return AGE_RULES[this.state.age]?.label || "";
    }

    renderManualPanel(ageRule, size) {
      const panel = this.querySelector("[data-pdp-manual-panel]");
      const notice = this.querySelector("[data-pdp-manual-notice]");

      panel.hidden = this.state.mode !== "manual";

      this.querySelectorAll("[data-pdp-style]").forEach((button) => {
        button.setAttribute("aria-pressed", button.dataset.pdpStyle === this.state.style ? "true" : "false");
      });

      this.querySelectorAll("[data-pdp-size]").forEach((button) => {
        button.setAttribute("aria-pressed", button.dataset.pdpSize === size ? "true" : "false");
      });

      if (notice) {
        if (this.state.style === "glass") {
          notice.hidden = false;
          notice.textContent = "The Glass bottle is a warming bottle; subscription is Wrapped Glass only.";
        } else if (this.state.mode === "manual" && size !== ageRule?.autoSize) {
          notice.hidden = false;
          notice.textContent = `Most ${ageRule.label} parents start with ${SIZE_LABEL[ageRule.autoSize]} / ${ageRule.autoSize}.`;
        } else {
          notice.hidden = true;
        }
      }
    }

    renderBenefits(size, flow) {
      const thisBenefits = this.querySelector("[data-pdp-this-benefits]");
      const nextBenefits = this.querySelector("[data-pdp-next-benefits]");
      const nextDelivery = this.querySelector("[data-pdp-next-delivery]");
      const inlineNote = this.querySelector("[data-pdp-inline-note]");
      const struck = !this.state.subscribe;

      thisBenefits.classList.toggle("is-struck", struck);
      thisBenefits.innerHTML = this.itemsToHtml([
        `2 Shielded Bottles(${SIZE_LABEL[size]}) with ${FLOW_LABEL[flow]} Nipples`,
        this.state.subscribe ? "10% off" : "Full one-time price",
        this.state.subscribe ? "FREE patent-pending venting tube" : "Venting tube available below",
        this.state.subscribe ? `FREE ${FLOW_LABEL[this.bonusFlow()]} nipple` : "No free nipple refresh"
      ]);

      if (this.state.subscribe) {
        const upgrade = AGE_RULES[this.state.age]?.upgradeNext;

        if (upgrade) {
          nextDelivery.hidden = false;
          inlineNote.hidden = true;
          const nextItems = [
            "240ml Wrapped Glass upgrade",
            "+ FREE SHIPPING",
            "Rapid nipples included"
          ];
          nextBenefits.classList.remove("is-struck");
          nextBenefits.innerHTML = this.itemsToHtml(nextItems);
        } else {
          nextDelivery.hidden = true;
          inlineNote.hidden = false;
          nextBenefits.innerHTML = "";
        }
      } else {
        nextDelivery.hidden = true;
        inlineNote.hidden = true;
        nextBenefits.innerHTML = "";
      }
    }

    itemsToHtml(items) {
      return items.map((item, index) => {
        const cls = index === 1 && !item.startsWith("+") ? "is-accent" : "";
        return `<li class="${cls}">${this.escapeHtml(item)}</li>`;
      }).join("");
    }

    buttonText(variant) {
      const price = variant?.price || 0;

      if (!this.state.subscribe) {
        return `add to cart - ${this.formatMoney(price)}`;
      }

      const subscriptionPrice = Math.round(price * 0.9);
      return `subscribe and add to cart - ${this.formatMoney(subscriptionPrice)} <s>${this.formatMoney(price)}</s>`;
    }

    syncFields() {
      const variant = this.currentVariant();
      const idInput = this.form?.querySelector('input[name="id"]');

      if (idInput && variant?.id) {
        idInput.value = variant.id;
        idInput.disabled = false;
      }

      this.setField("[data-pdp-selling-plan]", this.state.subscribe ? this.config.sellingPlanId : "");
      this.setField("[data-pdp-entry-age]", this.state.age);
      this.setField("[data-pdp-due-date]", this.state.age === "expecting" ? this.state.dueDate : "");
      this.setField("[data-pdp-initial-flow]", this.currentFlow());
      this.setField("[data-pdp-bonus-flow]", this.bonusFlow());
      this.setField("[data-pdp-manual-override]", this.state.mode === "manual" ? "true" : "false");
      this.setField("[data-pdp-duo-flag]", this.state.duo ? "true" : "false");
    }

    setField(selector, value) {
      const field = this.querySelector(selector);
      if (field) {
        field.value = value || "";
        field.disabled = !value;
      }
    }

    onSubmit(event) {
      this.syncFields();

      if (this.state.age === "expecting" && !this.state.dueDate) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openDueModal();
        return;
      }

      const extraItems = this.extraItems();
      if (!extraItems.length) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.submitBundle(extraItems);
    }

    extraItems() {
      const items = [];

      if (!this.state.subscribe && this.state.ventingAddon) {
        const ventingId = this.config.metafields?.ventingVariantId;
        if (ventingId) {
          items.push({ id: Number(ventingId), quantity: 1 });
        }
      }

      if (this.state.duo && this.state.style === "wrapped_glass") {
        const duoVariant = this.variantIdFromMetafield("glass", this.currentSize()) || this.findVariant("glass", this.currentSize())?.id;
        if (duoVariant) {
          items.push({ id: Number(duoVariant), quantity: 1 });
        }
      }

      return items;
    }

    async submitBundle(extraItems) {
      const submit = this.querySelector("[data-pdp-submit]");
      submit?.setAttribute("aria-busy", "true");
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      try {
        const mainItem = this.mainItemPayload();
        const sectionsToBundle = new Set();
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: JSON.stringify({
            items: [mainItem, ...extraItems],
            sections: [...sectionsToBundle].join(","),
            sections_url: window.location.pathname
          })
        });

        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.message || "Unable to add to cart");
        }

        if (window.themeVariables?.settings?.cartType === "page" || window.themeVariables?.settings?.pageType === "cart") {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cart.sections = responseJson.sections;
        this.form?.dispatchEvent(new CustomEvent("variant:add", {
          bubbles: true,
          detail: { items: responseJson.items || [responseJson], cart }
        }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: { baseEvent: "variant:add", cart }
        }));
      } catch (error) {
        document.dispatchEvent(new CustomEvent("toast:show", {
          detail: { message: error.message, tone: "error" }
        }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      } finally {
        submit?.removeAttribute("aria-busy");
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }

    mainItemPayload() {
      const variant = this.currentVariant();
      if (!variant?.id) {
        throw new Error("Please configure product variant IDs before adding to cart.");
      }

      const properties = {
        _entry_age: this.state.age,
        _initial_flow: this.currentFlow(),
        _bonus_nipple_flow: this.bonusFlow(),
        _manual_override: this.state.mode === "manual" ? "true" : "false",
        _duo_flag: this.state.duo ? "true" : "false"
      };

      if (this.state.age === "expecting") {
        properties._due_date = this.state.dueDate;
      }

      const item = {
        id: Number(variant.id),
        quantity: 1,
        properties
      };

      if (this.state.subscribe && this.config.sellingPlanId) {
        item.selling_plan = Number(this.config.sellingPlanId);
      }

      return item;
    }

    formatShortDate(value) {
      const date = new Date(`${value}T00:00:00`);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${month}/${day}`;
    }

    formatMoney(cents) {
      const format = this.config.moneyFormat || "${{amount}}";
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = String(Math.round(cents / 100));

      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, amountNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/, amount);
    }

    escapeHtml(value) {
      const element = document.createElement("div");
      element.textContent = value;
      return element.innerHTML;
    }
  }

  if (!window.customElements.get("pdp-configurator")) {
    window.customElements.define("pdp-configurator", PdpConfigurator);
  }
})();
